import { useAtomValue } from 'jotai'
import { autoLoadMediaAtom } from '@/atoms/preferences'
import { cn } from '@/lib/utils'
import { Event } from '@nostr/tools/wasm'
import { useMemo } from 'react'
import ExternalLink from '../ExternalLink'
import Image from '../Image'
import Username from '../Username'

type Author = { pubkey: string; role?: string }

export default function PodcastMetadata({
  event,
  className
}: {
  event: Event
  className?: string
}) {
  const autoLoadMedia = useAtomValue(autoLoadMediaAtom)
  const title = useMemo(() => event.tags.find((t) => t[0] === 'title')?.[1], [event])
  const image = useMemo(() => event.tags.find((t) => t[0] === 'image')?.[1], [event])
  const description = useMemo(() => event.tags.find((t) => t[0] === 'description')?.[1], [event])
  const websites = useMemo(
    () =>
      event.tags
        .filter((t) => t[0] === 'website')
        .map((t) => t[1])
        .filter(Boolean),
    [event]
  )
  const authors = useMemo<Author[]>(
    () =>
      event.tags
        .filter((t) => t[0] === 'p')
        .map((t) => ({ pubkey: t[1], role: t[2] }))
        .filter((a) => a.pubkey),
    [event]
  )

  return (
    <div className={cn('space-y-2', className)}>
      <div className="font-medium">Podcast</div>
      <div className="flex gap-4">
        {image && autoLoadMedia && (
          <Image
            imageUrl={image}
            imagePubkey={event.pubkey}
            className="aspect-square bg-foreground h-20"
            hideIfError
          />
        )}
        <div className="flex-1 w-0 space-y-1">
          {title && <div className="text-xl font-semibold line-clamp-1">{title}</div>}
          {description && (
            <div className="text-sm text-muted-foreground line-clamp-4">{description}</div>
          )}
        </div>
      </div>
      {websites.length > 0 && (
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Website: </span>
          {websites.map((url, i) => (
            <span key={i}>
              {i > 0 && <span>, </span>}
              <ExternalLink url={url} />
            </span>
          ))}
        </div>
      )}
      {authors.length > 0 && (
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Authors: </span>
          {authors.map((a, i) => (
            <span key={i}>
              {i > 0 && <span>, </span>}
              <Username
                user={a.pubkey}
                className="inline-block"
                skeletonClassName="inline-block h-4"
              />
              {a.role && <span className="ml-1">({a.role})</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
